import React, {useEffect, useMemo, useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {restRequest} from '../../../api-utils/apiQueries'
import {convertToHours} from '../../common-function/formatDateToYearMonth'
import {
  extractPaymentPayloadFields,
  PaymentDetailField,
} from './paymentHelpers'

const DetailField = ({label, value}: PaymentDetailField) => (
  <div className='mb-6'>
    <div className='text-muted fw-semibold fs-7 mb-1'>{label}</div>
    <div className='fw-semibold text-gray-900 text-break'>{value}</div>
  </div>
)

const PaymentDetails = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [detail, setDetail] = useState<any>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return
      setIsLoading(true)
      setError('')
      try {
        const response: any = await restRequest.getPaymentDetail(id)
        const payload = response?.data?.data
        if (payload) {
          setDetail(payload)
        } else {
          setError(response?.data?.message || 'Payment not found.')
          setDetail(null)
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Unable to load payment details.'
        setError(msg)
        setDetail(null)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDetail()
  }, [id])

  const {paymentFields, userFields, providerFields} = useMemo(() => {
    if (!detail) {
      return {paymentFields: [], userFields: [], providerFields: []}
    }

    const user = detail.user
    const createdLabel = detail.createdAt
      ? `${new Date(detail.createdAt).toLocaleDateString()} · ${convertToHours(new Date(detail.createdAt))}`
      : '—'

    const paymentFields: PaymentDetailField[] = [
      {label: 'Payment ID', value: String(detail.id ?? '—')},
      {
        label: 'Transaction type',
        value: detail.transaction_category ?? '—',
      },
      {
        label: 'Amount (AUD)',
        value: `$${Number(detail.display_amount ?? 0).toFixed(2)}`,
      },
      {label: 'Payment mode', value: detail.payment_mode ?? '—'},
      {label: 'Provider', value: detail.provider ?? '—'},
      {label: 'Transaction ID', value: detail.transaction_id ?? '—'},
      {
        label: 'Currency',
        value: detail.currency ? String(detail.currency).toUpperCase() : '—',
      },
      {label: 'Date & time', value: createdLabel},
    ]

    if (detail.card_number) {
      const masked = String(detail.card_number)
      paymentFields.push({
        label: 'Card reference',
        value: masked.length > 4 ? `**** ${masked.slice(-4)}` : masked,
      })
    }

    const userFields: PaymentDetailField[] = []
    if (user?.mobile) userFields.push({label: 'Mobile', value: user.mobile})
    if (user?.id) userFields.push({label: 'User ID', value: String(user.id)})

    const providerFields = extractPaymentPayloadFields(detail.payment_obj)

    return {paymentFields, userFields, providerFields}
  }, [detail])

  const user = detail?.user
  const statusBadgeClass =
    detail?.status === 'Approved'
      ? 'badge-light-success'
      : detail?.status === 'Cancel'
        ? 'badge-light-danger'
        : 'badge-light-warning'

  return (
    <div className='w-100'>
      <div className='d-flex align-items-center gap-3 mb-5'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => navigate('/payment')}
        >
          ← Back to Payment Management
        </button>
      </div>

      <p className='card-label fw-bold fs-3 mb-5'>Payment Details</p>

      {isLoading ? (
        <div className='d-flex justify-content-center mt-10'>
          <div className='spinner-border text-primary' role='status' />
        </div>
      ) : error || !detail ? (
        <div className='alert alert-danger'>{error || 'Payment not found.'}</div>
      ) : (
        <div className='card mb-10'>
          <div className='card-body pt-9 pb-9 px-9'>
            <div className='row g-10 g-xl-15'>
              <div className='col-lg-6 pe-lg-10 border-end-lg'>
                <h4 className='fw-bold text-gray-800 mb-2'>Payment</h4>
                <p className='text-muted fs-7 mb-8'>Core transaction information</p>
                {paymentFields.map((f) => (
                  <DetailField key={f.label} {...f} />
                ))}
                {detail.status && (
                  <div className='mb-6'>
                    <div className='text-muted fw-semibold fs-7 mb-1'>Status badge</div>
                    <span className={`badge ${statusBadgeClass}`}>{detail.status}</span>
                  </div>
                )}
              </div>

              <div className='col-lg-6 ps-lg-10'>
                <h4 className='fw-bold text-gray-800 mb-2'>User & provider</h4>
                <p className='text-muted fs-7 mb-8'>Account and provider-specific data</p>

                <div className='mb-8'>
                  {user?.id ? (
                    <Link
                      to={`/user-management/${user.id}`}
                      className='fs-3 fw-bold text-gray-900 text-hover-primary d-block'
                    >
                      {user.name || 'View user profile'}
                    </Link>
                  ) : (
                    <span className='fs-3 fw-bold text-gray-900 d-block'>
                      {user?.name ?? '—'}
                    </span>
                  )}
                  <span className='text-muted fs-6 d-block mt-1'>
                    {user?.email ?? '—'}
                  </span>
                </div>

                {userFields.map((f) => (
                  <DetailField key={f.label} {...f} />
                ))}

                {providerFields.length > 0 && (
                  <>
                    <h5 className='fw-bold text-gray-800 mb-4 mt-8'>
                      Provider transaction details
                    </h5>
                    {providerFields.map((f) => (
                      <DetailField key={`provider-${f.label}`} {...f} />
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentDetails
